import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Mail, 
  Phone, 
  MapPin, 
  Calendar, 
  Package,
  CreditCard,
  User,
  Ban,
  CheckCircle,
  Clock,
  TrendingUp
} from 'lucide-react';
import { Customer } from '@/types/customer';
import { Order } from '@/types/order';
import { customerApi } from '@/services/customerApi';

export const CustomerDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    if (id) {
      loadCustomerData(id);
    }
  }, [id]);

  const loadCustomerData = async (customerId: string) => {
    try {
      setLoading(true);
      const [customerData, ordersData] = await Promise.all([
        customerApi.getCustomer(customerId),
        customerApi.getCustomerOrders(customerId),
      ]);
      setCustomer(customerData);
      setOrders(ordersData);
    } catch (error) {
      console.error('Error loading customer data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (status: 'active' | 'inactive' | 'blocked') => {
    if (!id || !customer) return;
    
    try {
      setUpdatingStatus(true);
      await customerApi.updateCustomerStatus(id, status);
      setCustomer({ ...customer, status });
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Durum güncellenirken bir hata oluştu');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const formatDate = (dateValue?: string | Date) => {
    if (!dateValue) return '-';
    const date = new Date(dateValue);
    return date.toLocaleDateString('tr-TR', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('tr-TR', { 
      style: 'currency', 
      currency: 'TRY' 
    }).format(amount);
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      awaiting_payment: { label: 'Ödeme Bekleniyor', className: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300' },
      pending: { label: 'Bekliyor', className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300' },
      confirmed: { label: 'Onaylandı', className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' },
      preparing: { label: 'Hazırlanıyor', className: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300' },
      shipping: { label: 'Kargoda', className: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300' },
      delivered: { label: 'Teslim Edildi', className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' },
      cancelled: { label: 'İptal', className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300' },
    };
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.className}`}>
        {config.label}
      </span>
    );
  };

  const getPaymentStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { label: 'Bekliyor', className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300' },
      paid: { label: 'Ödendi', className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' },
      refunded: { label: 'İade Edildi', className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300' },
    };
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.className}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400 mb-4">Müşteri bulunamadı</p>
        <button
          onClick={() => navigate('/admin/customers')}
          className="btn-primary"
        >
          Müşteriler Listesine Dön
        </button>
      </div>
    );
  }

  const totalSpent = orders.reduce((sum, order) => sum + (order.totalPrice || 0), 0);
  const completedOrders = orders.filter(o => o.status === 'delivered').length;
  const pendingOrders = orders.filter(o => ['pending', 'preparing'].includes(o.status)).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/customers')}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-dark dark:text-light">Müşteri Detayı</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">{customer.name}</p>
          </div>
        </div>
        
        {/* Status Actions */}
        <div className="flex gap-2">
          {customer.status !== 'active' && (
            <button
              onClick={() => handleStatusUpdate('active')}
              disabled={updatingStatus}
              className="px-4 py-2 bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 rounded-lg hover:bg-green-200 dark:hover:bg-green-800 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              <CheckCircle className="w-4 h-4" />
              Aktif Et
            </button>
          )}
          {customer.status !== 'blocked' && (
            <button
              onClick={() => handleStatusUpdate('blocked')}
              disabled={updatingStatus}
              className="px-4 py-2 bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300 rounded-lg hover:bg-red-200 dark:hover:bg-red-800 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              <Ban className="w-4 h-4" />
              Engelle
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Toplam Sipariş</p>
              <p className="text-3xl font-bold text-dark dark:text-light mt-2">{orders.length}</p>
            </div>
            <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300">
              <Package className="w-6 h-6" />
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Tamamlanan</p>
              <p className="text-3xl font-bold text-dark dark:text-light mt-2">{completedOrders}</p>
            </div>
            <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-300">
              <CheckCircle className="w-6 h-6" />
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Devam Eden</p>
              <p className="text-3xl font-bold text-dark dark:text-light mt-2">{pendingOrders}</p>
            </div>
            <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-yellow-100 dark:bg-yellow-900 text-yellow-600 dark:text-yellow-300">
              <Clock className="w-6 h-6" />
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Toplam Harcama</p>
              <p className="text-2xl font-bold text-dark dark:text-light mt-2">{formatCurrency(totalSpent)}</p>
            </div>
            <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-300">
              <TrendingUp className="w-6 h-6" />
            </div>
          </div>
        </div>
      </div>

      {/* Customer Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Basic Info */}
        <div className="card">
          <h2 className="text-xl font-semibold text-dark dark:text-light mb-4 flex items-center gap-2">
            <User className="w-5 h-5" />
            Müşteri Bilgileri
          </h2>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Mail className="w-5 h-5 text-gray-400 mt-1" />
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Email</p>
                <p className="text-gray-900 dark:text-gray-100">{customer.email}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Phone className="w-5 h-5 text-gray-400 mt-1" />
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Telefon</p>
                <p className="text-gray-900 dark:text-gray-100">{customer.phone}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-gray-400 mt-1" />
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Kayıt Tarihi</p>
                <p className="text-gray-900 dark:text-gray-100">{formatDate(customer.createdAt)}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-gray-400 mt-1" />
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Durum</p>
                <p className="text-gray-900 dark:text-gray-100">
                  {customer.status === 'active' ? 'Aktif' : customer.status === 'blocked' ? 'Engelli' : 'Pasif'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Addresses */}
        <div className="card">
          <h2 className="text-xl font-semibold text-dark dark:text-light mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5" />
            Adresler ({customer.addresses?.length || 0})
          </h2>
          <div className="space-y-4">
            {customer.addresses && customer.addresses.length > 0 ? (
              customer.addresses.map((address, index) => (
                <div 
                  key={`address-${index}`} 
                  className={`p-4 rounded-lg border-2 ${
                    address.isDefault 
                      ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800' 
                      : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="text-gray-900 dark:text-gray-100 font-medium">
                        {address.title || 'Adres'}
                      </p>
                      {address.isDefault && (
                        <span className="mt-1 inline-block px-2 py-0.5 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300 text-xs font-medium rounded">
                          Varsayılan
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="space-y-1 text-sm">
                    <p className="text-gray-900 dark:text-gray-100">{address.details}</p>
                    <p className="text-gray-600 dark:text-gray-400">
                      {address.district}, {address.city}, {address.province}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500 dark:text-gray-400">Kayıtlı adres bulunmuyor</p>
            )}
          </div>
        </div>
      </div>

      {/* Orders Section */}
      <div className="card">
        <h2 className="text-xl font-semibold text-dark dark:text-light mb-4 flex items-center gap-2">
          <Package className="w-5 h-5" />
          Siparişler ({orders.length})
        </h2>
        
        {orders.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            Henüz sipariş bulunmuyor
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Sipariş No
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Tarih
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Ürünler
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Tutar
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Ödeme
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Durum
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    İşlem
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                {orders.map((order) => (
                  <tr 
                    key={order.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        #{order.id.substring(0, 8)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-gray-100">
                        {formatDate(order.createdAt)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 dark:text-gray-100">
                        {order.items.length} ürün
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {order.items.slice(0, 2).map(item => item.title).join(', ')}
                        {order.items.length > 2 && '...'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {formatCurrency(order.totalPrice)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col gap-1">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {order.paymentInfo?.method === 'credit-card' ? 'Kredi Kartı' : order.paymentInfo?.method || '-'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(order.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => navigate(`/admin/orders/${order.id}`)}
                        className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                      >
                        Detay
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
